import React from 'react';

import {
  AccountCircle as AccountCircleIcon,
  DynamicFeed as DynamicFeedIcon,
  PowerSettingsNew as PowerSettingsIcon,
} from '@mui/icons-material';
import { Box, Divider, List, ListItem, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { appRoutes, DrawerTabs } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { NavListItem, TabItem, TabItemKey, Tabs } from 'src/components/common';
import { IS_PRODUCTION } from 'src/config';
import { useAppBuildVersions } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { OpenCloseStateResult } from 'src/hooks/shared';
import { MenuItem } from 'src/types/menu';

import * as styles from './styles';

interface BaseLayoutLeftMenuProps {
  menuItemsList: Array<Nullable<MenuItem>>;
  myAccountItemsList?: Array<Nullable<MenuItem>>;
  rootRoute: string;
  logoutRoute: string;
  leftMenuDrawerState: OpenCloseStateResult<Record<string, never>>;
  leftMenuLogo?: React.ReactNode;
  footer?: React.ReactNode;
  disabled?: boolean;
  hideLogo?: boolean;
}

export const BaseLayoutLeftMenu: React.FC<BaseLayoutLeftMenuProps> = ({
  menuItemsList,
  rootRoute,
  logoutRoute,
  leftMenuDrawerState,
  leftMenuLogo,
  footer,
  disabled,
  myAccountItemsList = [],
  hideLogo = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { isMobile } = useScreenInfo();
  const { serverBuildVersion, clientBuildVersion } = useAppBuildVersions(IS_PRODUCTION);

  const isExactHomePath = React.useMemo(
    () => !!matchPath({ path: rootRoute, end: false }, location.pathname),
    [location.pathname, rootRoute],
  );
  const isMyAccountPath = React.useMemo(
    () => !!matchPath({ path: appRoutes.account.index, end: false }, location.pathname),
    [location.pathname],
  );

  const [currentTabKey, setCurrentTabKey] = React.useState(
    isMyAccountPath ? DrawerTabs.MyAccount : DrawerTabs.Pages,
  );

  const menuItems = React.useMemo(() => {
    return menuItemsList
      .filter(linkObj => !linkObj?.hide)
      .filter(isNotNil)
      .map(linkObj => {
        return (
          <NavListItem
            key={linkObj.link}
            sx={styles.navListItemStyles}
            link={linkObj.link}
            selected={linkObj.link === rootRoute && isExactHomePath ? true : undefined}
            onClick={() => (isMobile ? leftMenuDrawerState.close() : null)}
            dense={false}
            disableGutters={false}
            divider={false}
            disabled={disabled || linkObj.disabled}
            startIcon={<NavListItem.Icon sx={styles.listItemIconStyles} icon={linkObj.icon} />}
            endIcon={
              linkObj.endIcon && (
                <NavListItem.Icon sx={styles.listItemEndIconStyles} icon={linkObj.endIcon} />
              )
            }
            listItemText={
              leftMenuDrawerState.isOpened && (
                <NavListItem.Text sx={styles.listItemTextStyles}>{linkObj.title}</NavListItem.Text>
              )
            }
          />
        );
      });
  }, [menuItemsList, rootRoute, isExactHomePath, disabled, leftMenuDrawerState, isMobile]);

  const myAccountItems = React.useMemo(() => {
    return myAccountItemsList
      .filter(linkObj => !linkObj?.hide)
      .filter(isNotNil)
      .map(linkObj => {
        return (
          <NavListItem
            key={linkObj.link}
            sx={styles.navListItemStyles}
            link={linkObj.link}
            onClick={() => (isMobile ? leftMenuDrawerState.close() : null)}
            selected={linkObj.link === rootRoute && isExactHomePath ? true : undefined}
            dense={false}
            disableGutters={false}
            divider={false}
            disabled={disabled || linkObj.disabled}
            startIcon={<NavListItem.Icon sx={styles.listItemIconStyles} icon={linkObj.icon} />}
            endIcon={
              linkObj.endIcon && (
                <NavListItem.Icon sx={styles.listItemEndIconStyles} icon={linkObj.endIcon} />
              )
            }
            listItemText={
              leftMenuDrawerState.isOpened && (
                <NavListItem.Text sx={styles.listItemTextStyles}>{linkObj.title}</NavListItem.Text>
              )
            }
          />
        );
      });
  }, [myAccountItemsList, isMobile, leftMenuDrawerState, rootRoute, isExactHomePath, disabled]);

  const handleTabChange = React.useCallback((tabKey: TabItemKey) => {
    if (isDrawerTabKey(tabKey)) {
      setCurrentTabKey(DrawerTabs[tabKey]);
    }
  }, []);

  const renderMobileFooter = React.useCallback(() => {
    const handleSignOutClick = () => {
      navigate(logoutRoute);
    };

    return (
      <ListItem sx={{ pl: 3, py: 2, cursor: 'pointer' }} onClick={handleSignOutClick}>
        <PowerSettingsIcon color="action" />
        <Typography variant="body1" ml={4} sx={styles.signOutButtonStyles}>
          Sign Out
        </Typography>
      </ListItem>
    );
  }, [logoutRoute, navigate]);

  const renderAppBuildVersions = React.useCallback(() => {
    if (IS_PRODUCTION) return null;
    return (
      <Box sx={styles.versionBoxStyles}>
        <React.Activity mode={clientBuildVersion ? 'visible' : 'hidden'}>
          <Typography sx={styles.versionLabelStyles}>client: {clientBuildVersion}</Typography>
        </React.Activity>
        <React.Activity mode={serverBuildVersion ? 'visible' : 'hidden'}>
          <Typography sx={styles.versionLabelStyles}>server: {serverBuildVersion}</Typography>
        </React.Activity>
      </Box>
    );
  }, [clientBuildVersion, serverBuildVersion]);

  const drawerTabs: Array<TabItem<DrawerTabs>> = React.useMemo(
    () => [
      {
        key: DrawerTabs.Pages,
        label: 'Pages',
        icon: <DynamicFeedIcon />,
        component: (
          <List dense={false} disablePadding={false}>
            {menuItems}
          </List>
        ),
      },
      {
        key: DrawerTabs.MyAccount,
        label: 'My Account',
        icon: <AccountCircleIcon />,
        component: (
          <List dense={false} disablePadding={false}>
            {myAccountItems}
          </List>
        ),
      },
    ],
    [menuItems, myAccountItems],
  );

  if (isMobile) {
    return (
      <Box sx={styles.mobileSidebarStyles}>
        <Box>
          <Tabs
            tabs={drawerTabs}
            currentTabKey={currentTabKey}
            onChange={handleTabChange}
            containerProps={{ sx: styles.tabsContainerStyles }}
          />
        </Box>
        {currentTabKey === DrawerTabs.MyAccount ? (
          <List dense disablePadding>
            <Divider sx={{ margin: '0 16px' }} />
            {renderMobileFooter()}
          </List>
        ) : (
          <List dense disablePadding>
            <Divider sx={{ margin: '0 16px' }} />
            {footer}
          </List>
        )}
      </Box>
    );
  }

  return (
    <Box sx={styles.sidebarStyles}>
      <Box sx={hideLogo ? styles.wrapperStylesHideLogo : styles.wrapperStyles}>
        <React.Activity mode={hideLogo ? 'hidden' : 'visible'}>
          <Box sx={styles.logoStyles}>{leftMenuLogo}</Box>
        </React.Activity>

        <List dense={false} disablePadding={false}>
          {menuItems}
        </List>
      </Box>

      <Box>
        <React.Activity mode={leftMenuDrawerState.isOpened ? 'visible' : 'hidden'}>
          {renderAppBuildVersions()}
        </React.Activity>

        <React.Activity mode={footer ? 'visible' : 'hidden'}>
          <List dense disablePadding>
            <Divider />
            {footer}
          </List>
        </React.Activity>
      </Box>
    </Box>
  );
};

const isDrawerTabKey = (tabKey: TabItemKey): tabKey is keyof typeof DrawerTabs => {
  return tabKey in DrawerTabs;
};
